// ============================================================================
// FuelEmergencyModal.jsx - FUEL EMERGENCY ALERT
// ============================================================================
//
// Shows when a flight runs out of fuel mid-flight due to range exceeded.
// Two severity levels:
// - "bad" (within 100nm of airport): Yellow/orange - successful divert
// - "catastrophic" (beyond 100nm): Red - crash landing with severe penalties
// ============================================================================

import React from "react";
import { Button } from "@/components/ui/button";
// Icons removed - text-only UI

export default function FuelEmergencyModal({
  flight,
  consequences,
  onClose,
}) {
  if (!flight || !consequences) return null;

  const isCatastrophic = consequences.severity === "catastrophic";
  const accentColor = isCatastrophic ? "#ef4444" : "#f59e0b"; // Red for catastrophic, orange for bad
  const bgTint = isCatastrophic ? "#1f0a0a" : "#1c1302";

  const formatCurrency = (amount) => {
    if (!amount) return "$0";
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
    return `$${amount.toLocaleString()}`;
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div
        className="bg-gray-900 rounded-lg max-w-lg w-full overflow-hidden"
        style={{ border: `2px solid ${accentColor}` }}
      >
        {/* Header */}
        <div
          className="p-6 text-center"
          style={{ backgroundColor: bgTint, borderBottom: `1px solid ${accentColor}40` }}
        >
          <div
            className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 text-2xl font-bold"
            style={{ backgroundColor: `${accentColor}20`, color: accentColor }}
          >
            {isCatastrophic ? "XXX" : "!"}
          </div>
          <h1 className="text-2xl font-bold" style={{ color: accentColor }}>
            {isCatastrophic ? "AIRCRAFT EMERGENCY" : "FUEL EMERGENCY"}
          </h1>
          <p className="text-lg font-mono mt-2" style={{ color: "#fff" }}>
            {flight.flightNumber}
          </p>
        </div>

        {/* Flight info */}
        <div className="p-6 space-y-4">
          {/* Route */}
          <div
            className="p-4 rounded-lg flex items-center justify-between"
            style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}
          >
            <span className="text-xl font-bold" style={{ color: "#fff" }}>
              {flight.origin}
            </span>
            <div className="flex items-center gap-2">
              <div className="w-16 h-0.5" style={{ backgroundColor: accentColor }} />
              <span className="text-xs font-bold" style={{ color: accentColor }}>--X--</span>
              <div className="w-16 h-0.5" style={{ backgroundColor: "#333" }} />
            </div>
            <span className="text-xl font-bold" style={{ color: "#737373" }}>
              {flight.destination}
            </span>
          </div>

          {/* What happened */}
          <div
            className="p-4 rounded-lg"
            style={{ backgroundColor: bgTint, border: `1px solid ${accentColor}40` }}
          >
            <div className="flex items-start gap-3">
              <span className="font-bold flex-shrink-0" style={{ color: accentColor }}>!</span>
              <div className="text-sm" style={{ color: "#e5e5e5" }}>
                {isCatastrophic ? (
                  <>
                    <strong>Aircraft ran out of fuel over remote area.</strong>
                    <br />
                    Emergency off-airport landing required. No airports within 100nm.
                  </>
                ) : (
                  <>
                    <strong>Aircraft ran out of fuel mid-flight.</strong>
                    <br />
                    Emergency diversion to nearest airport.
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Nearest airport */}
          <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: "#1a1a1a" }}>
            <span className="text-xs font-bold" style={{ color: "#3b82f6" }}>APT</span>
            <div>
              <span style={{ color: "#737373" }}>
                {isCatastrophic ? "Nearest airport was:" : "Diverted to:"}
              </span>
              <span className="ml-2 font-bold" style={{ color: "#fff" }}>
                {consequences.nearestAirport}
              </span>
              <span className="ml-2" style={{ color: "#737373" }}>
                ({consequences.distanceToNearestAirportNm} nm away)
              </span>
            </div>
          </div>

          {/* Consequences */}
          <div className="space-y-2">
            <h3 className="text-sm font-bold uppercase" style={{ color: "#737373" }}>
              Consequences
            </h3>

            {/* Financial */}
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 rounded" style={{ backgroundColor: "#1a1a1a" }}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold" style={{ color: "#ef4444" }}>$</span>
                  <span className="text-xs" style={{ color: "#737373" }}>Total Cost</span>
                </div>
                <div className="text-lg font-bold" style={{ color: "#ef4444" }}>
                  {formatCurrency(consequences.totalCost)}
                </div>
              </div>

              <div className="p-3 rounded" style={{ backgroundColor: "#1a1a1a" }}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold" style={{ color: "#f59e0b" }}>REP</span>
                  <span className="text-xs" style={{ color: "#737373" }}>Reputation</span>
                </div>
                <div className="text-lg font-bold" style={{ color: "#f59e0b" }}>
                  {consequences.reputationHit}
                </div>
              </div>
            </div>

            {/* Cost breakdown */}
            <div className="p-3 rounded text-xs space-y-1" style={{ backgroundColor: "#0c0c0c" }}>
              <div className="flex justify-between" style={{ color: "#a1a1a1" }}>
                <span>FAA Penalty</span>
                <span style={{ color: "#ef4444" }}>{formatCurrency(consequences.breakdown?.faaPenalty)}</span>
              </div>
              <div className="flex justify-between" style={{ color: "#a1a1a1" }}>
                <span>Passenger Compensation ({consequences.affectedPassengers} pax)</span>
                <span style={{ color: "#ef4444" }}>{formatCurrency(consequences.breakdown?.passengerCompensation)}</span>
              </div>
              <div className="flex justify-between" style={{ color: "#a1a1a1" }}>
                <span>Hotel & Meals</span>
                <span style={{ color: "#ef4444" }}>{formatCurrency(consequences.breakdown?.hotelMeals)}</span>
              </div>
              <div className="flex justify-between" style={{ color: "#a1a1a1" }}>
                <span>Aircraft Recovery</span>
                <span style={{ color: "#ef4444" }}>{formatCurrency(consequences.breakdown?.aircraftRecoveryCost)}</span>
              </div>
              <div className="flex justify-between" style={{ color: "#a1a1a1" }}>
                <span>Media Settlement</span>
                <span style={{ color: "#ef4444" }}>{formatCurrency(consequences.breakdown?.mediaSettlement)}</span>
              </div>
            </div>

            {/* Aircraft damage */}
            {consequences.aircraftDamagePercent > 0 && (
              <div
                className="p-3 rounded flex items-center justify-between"
                style={{ backgroundColor: "#1f0a0a", border: "1px solid #ef444440" }}
              >
                <span className="text-sm" style={{ color: "#fca5a5" }}>
                  Aircraft Condition Degraded
                </span>
                <span className="font-bold" style={{ color: "#ef4444" }}>
                  -{consequences.aircraftDamagePercent}%
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t" style={{ borderColor: "#333" }}>
          <Button
            onClick={onClose}
            className="w-full"
            style={{ backgroundColor: accentColor, color: isCatastrophic ? "#fff" : "#000" }}
          >
            [X] Acknowledge
          </Button>
        </div>
      </div>
    </div>
  );
}
